// Georeferencing: 3 control points {x,y,lat,lon} -> affine transform (lat/lon -> pixel).
// Uses a local equirectangular projection anchored at the first control point.

const M_PER_DEG_LAT = 111132;
const M_PER_DEG_LON = 111320;

function toLocal(lat, lon, lat0, lon0) {
  const e = (lon - lon0) * M_PER_DEG_LON * Math.cos((lat0 * Math.PI) / 180);
  const n = (lat - lat0) * M_PER_DEG_LAT;
  return { e, n };
}

// Solve a 3x3 linear system m*x = v via Cramer's rule. Returns null if singular.
function solve3(m, v) {
  const det = (a) =>
    a[0][0] * (a[1][1] * a[2][2] - a[1][2] * a[2][1]) -
    a[0][1] * (a[1][0] * a[2][2] - a[1][2] * a[2][0]) +
    a[0][2] * (a[1][0] * a[2][1] - a[1][1] * a[2][0]);
  const D = det(m);
  if (Math.abs(D) < 1e-9) return null;
  const col = (i) => m.map((row, r) => row.map((val, c) => (c === i ? v[r] : val)));
  return [0, 1, 2].map((i) => det(col(i)) / D);
}

export function computeGeoTransform(points) {
  if (!points || points.length < 3) return null;
  const p = points.slice(0, 3);
  if (p.some((q) => q == null || q.lat == null || q.lon == null || q.x == null || q.y == null)) return null;
  const lat0 = p[0].lat;
  const lon0 = p[0].lon;
  const locals = p.map((q) => toLocal(q.lat, q.lon, lat0, lon0));
  const M = locals.map((l) => [l.e, l.n, 1]);
  const ax = solve3(M, p.map((q) => q.x)); // x = ax[0]*e + ax[1]*n + ax[2]
  const ay = solve3(M, p.map((q) => q.y)); // y = ay[0]*e + ay[1]*n + ay[2]
  if (!ax || !ay) return null;
  const sx = Math.hypot(ax[0], ay[0]);
  const sy = Math.hypot(ax[1], ay[1]);
  const pxPerMeter = (sx + sy) / 2;
  return {
    lat0,
    lon0,
    pxPerMeter,
    latlonToPixel(lat, lon) {
      const { e, n } = toLocal(lat, lon, lat0, lon0);
      return { x: ax[0] * e + ax[1] * n + ax[2], y: ay[0] * e + ay[1] * n + ay[2] };
    },
  };
}

function haversineM(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Internal-consistency quality of a 3-point calibration: compares the real GPS
// distance between each pair of control points with the on-map (pixel) distance.
// With an exact affine the control points reproduce perfectly, so we instead
// surface how self-consistent the three points are (a good proxy for GPS noise /
// misplacement). Returns null when not enough points.
export function computeCalibrationQuality(points) {
  if (!points || points.length < 3) return null;
  const p = points.slice(0, 3);
  if (p.some((q) => q == null || q.lat == null || q.lon == null || q.x == null || q.y == null)) return null;
  const pairs = [[0, 1], [0, 2], [1, 2]];
  const edges = pairs.map(([i, j]) => {
    const gpsM = haversineM(p[i], p[j]);
    const pxDist = Math.hypot(p[i].x - p[j].x, p[i].y - p[j].y);
    return { i, j, gpsM, pxDist, scale: gpsM > 0 ? pxDist / gpsM : 0 };
  });
  if (edges.some((e) => e.gpsM < 0.5)) return { degenerate: true };
  const avgScale = edges.reduce((s, e) => s + e.scale, 0) / edges.length;
  let maxRel = 0;
  let maxAbsM = 0;
  const detailed = edges.map((e) => {
    const predM = avgScale > 0 ? e.pxDist / avgScale : 0;
    const absErr = Math.abs(predM - e.gpsM);
    const relErr = avgScale > 0 ? Math.abs(e.scale - avgScale) / avgScale : 0;
    maxAbsM = Math.max(maxAbsM, absErr);
    maxRel = Math.max(maxRel, relErr);
    return { ...e, predM, absErr };
  });
  const rating = maxAbsM < 3 ? "good" : maxAbsM < 8 ? "ok" : "poor";
  const accuracies = p.map((q) => q.accuracy).filter((a) => a != null);
  const worstAccuracy = accuracies.length ? Math.max(...accuracies) : null;
  return { edges: detailed, avgScale, maxRel, maxAbsM, rating, worstAccuracy };
}

